import { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
    MapPin, 
    Clock, 
    Phone, 
    Instagram, 
    Mail, 
    MessageCircle,
    ChevronDown,
    Star,
    Sparkles,
    Zap,
    Menu,
    X
} from "lucide-react";

const WHATSAPP_NUMBER = "5521972232170";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}`;
const WHATSAPP_MESSAGE = encodeURIComponent("Olá! Gostaria de ver o cardápio e fazer um pedido!");
const WHATSAPP_FULL_LINK = `${WHATSAPP_LINK}?text=${WHATSAPP_MESSAGE}`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c0653a22-b7c8-4837-806e-2981b0534f96/artifacts/5qld58kk_logo%20comida%20japonesa%20delivery.png";

const BACKGROUND_MUSIC_URL = "https://customer-assets.emergentagent.com/job_futuristic-sashimi/artifacts/2y6ugmoc_Amazing%20Oriental%20Music%20-%20HD%20-%20FARAN%20ENSEMBLE%20%5B2X16HWbr87A%5D%20%28mp3cut.net%29.mp3";

const IMAGES = {
    hero: "https://images.unsplash.com/photo-1705734810394-1d3af2413c87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjeWJlcnB1bmslMjB0b2t5byUyMHN0cmVldCUyMGZvb2QlMjBzdGFsbCUyMG5pZ2h0JTIwcmFpbnxlbnwwfHx8fDE3NzMzNTQ3NjB8MA&ixlib=rb-4.1.0&q=85",
    sashimi: "https://images.unsplash.com/photo-1675870792392-116a80bd7ad6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxzYXNoaW1pJTIwc2FsbW9uJTIwY2xvc2UlMjB1cCUyMGRhcmslMjBkcmFtYXRpYyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3MzM1NDc1OHww&ixlib=rb-4.1.0&q=85",
    hotRoll: "https://images.unsplash.com/photo-1743110727495-096e8e3914af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxzYXNoaW1pJTIwc2FsbW9uJTIwY2xvc2UlMjB1cCUyMGRhcmslMjBkcmFtYXRpYyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3MzM1NDc1OHww&ixlib=rb-4.1.0&q=85",
    gallery1: "https://images.unsplash.com/photo-1747471447806-c56d99c348cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxzYXNoaW1pJTIwc2FsbW9uJTIwY2xvc2UlMjB1cCUyMGRhcmslMjBkcmFtYXRpYyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3MzM1NDc1OHww&ixlib=rb-4.1.0&q=85",
    gallery2: "https://images.unsplash.com/photo-1735315050688-010b5b548054?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxzYXNoaW1pJTIwc2FsbW9uJTIwY2xvc2UlMjB1cCUyMGRhcmslMjBkcmFtYXRpYyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3MzM1NDc1OHww&ixlib=rb-4.1.0&q=85",
    gallery3: "https://images.unsplash.com/photo-1714182370392-a32d43080717?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxjeWJlcnB1bmslMjB0b2t5byUyMHN0cmVldCUyMGZvb2QlMjBzdGFsbCUyMG5pZ2h0JTIwcmFpbnxlbnwwfHx8fDE3NzMzNTQ3NjB8MA&ixlib=rb-4.1.0&q=85",
};

const DELIVERY_AREAS = [
    "Barra da Tijuca",
    "Recreio dos Bandeirantes",
    "Jacarepaguá",
    "Freguesia",
    "Cidade de Deus"
];

const TESTIMONIALS = [
    {
        name: "Marina S.",
        text: "Melhor sashimi que já comi na Barra! Fresquíssimo e entrega super rápida. Virei cliente fiel!",
        rating: 5,
        area: "Barra da Tijuca"
    },
    {
        name: "Rafael O.",
        text: "Hot Philadelphia simplesmente perfeito! Crocante por fora, cremoso por dentro. Preço imbatível!",
        rating: 5,
        area: "Recreio"
    },
    {
        name: "Carla M.",
        text: "Atendimento 24h é vida! Pedi 2h da manhã e chegou quentinho. Sensacional!",
        rating: 5,
        area: "Jacarepaguá"
    }
];

const NeonButton = ({ children, onClick, className = "", href, dataTestId }) => {
    const handleClick = () => {
        if (href) {
            window.open(href, '_blank');
        }
        if (onClick) {
            onClick();
        }
    };

    return (
        <motion.button
            data-testid={dataTestId}
            className={`neon-btn ${className}`}
            onClick={handleClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
        >
            <span className="neon-btn-content">{children}</span>
        </motion.button>
    );
};

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <motion.header
            data-testid="header"
            className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg"
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    <motion.a 
                        href="/"
                        className="flex items-center"
                        whileHover={{ scale: 1.05 }}
                    >
                        <img 
                            src={LOGO_URL} 
                            alt="Comida Japonesa Delivery" 
                            className="h-14 md:h-16 w-auto"
                            data-testid="logo"
                        />
                    </motion.a>

                    <nav className="hidden md:flex items-center gap-8">
                        <a href="#ofertas" className="font-rajdhani text-gray-800 hover:text-neon-red transition-colors uppercase tracking-wider text-sm font-semibold">
                            Ofertas
                        </a>
                        <a href="#delivery" className="font-rajdhani text-gray-800 hover:text-neon-red transition-colors uppercase tracking-wider text-sm font-semibold">
                            Delivery
                        </a>
                        <a href="#galeria" className="font-rajdhani text-gray-800 hover:text-neon-red transition-colors uppercase tracking-wider text-sm font-semibold">
                            Galeria
                        </a>
                        <a href="#contato" className="font-rajdhani text-gray-800 hover:text-neon-red transition-colors uppercase tracking-wider text-sm font-semibold">
                            Contato
                        </a>
                    </nav>

                    <div className="hidden md:block">
                        <NeonButton href={WHATSAPP_FULL_LINK} dataTestId="header-cta-whatsapp">
                            <MessageCircle size={18} />
                            Pedir Agora
                        </NeonButton>
                    </div>

                    <button 
                        className="md:hidden text-gray-800 p-2"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        className="md:hidden bg-white border-t border-gray-200"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                    >
                        <nav className="flex flex-col p-4 gap-4">
                            <a href="#ofertas" className="font-rajdhani text-gray-800 hover:text-neon-red transition-colors uppercase tracking-wider py-2 font-semibold" onClick={() => setMobileMenuOpen(false)}>
                                Ofertas
                            </a>
                            <a href="#delivery" className="font-rajdhani text-gray-800 hover:text-neon-red transition-colors uppercase tracking-wider py-2 font-semibold" onClick={() => setMobileMenuOpen(false)}>
                                Delivery
                            </a>
                            <a href="#galeria" className="font-rajdhani text-gray-800 hover:text-neon-red transition-colors uppercase tracking-wider py-2 font-semibold" onClick={() => setMobileMenuOpen(false)}>
                                Galeria
                            </a>
                            <a href="#contato" className="font-rajdhani text-gray-800 hover:text-neon-red transition-colors uppercase tracking-wider py-2 font-semibold" onClick={() => setMobileMenuOpen(false)}>
                                Contato
                            </a>
                            <NeonButton href={WHATSAPP_FULL_LINK} dataTestId="mobile-cta-whatsapp" className="mt-2">
                                <MessageCircle size={18} />
                                Pedir Agora
                            </NeonButton>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="h-[2px] bg-gradient-to-r from-transparent via-neon-red to-transparent" />
        </motion.header>
    );
};

const HeroSection = () => {
    return (
        <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
            <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${IMAGES.hero})` }}
            />
            <div className="hero-overlay" />
            <div className="absolute inset-0 cyber-grid opacity-30" />

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
                <motion.div
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <span className="pulse-dot" />
                        <span className="font-rajdhani text-neon-green text-sm uppercase tracking-widest">
                            Aberto 24 Horas
                        </span>
                    </div>
                    <h1 className="font-orbitron text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-4 tracking-wider">
                        <span className="neon-text-red">SUSHI</span> DO FUTURO
                    </h1>
                    <p className="font-chakra text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-2">
                        Delivery Premium na Barra da Tijuca e região
                    </p>
                    <p className="font-rajdhani text-neon-cyan text-sm uppercase tracking-widest">
                        Recreio • Jacarepaguá • Freguesia
                    </p>
                </motion.div>

                <motion.div
                    className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                >
                    <div className="product-card p-6 group" data-testid="offer-sashimi">
                        <div className="relative overflow-hidden mb-4">
                            <img 
                                src={IMAGES.sashimi} 
                                alt="20 Sashimis de Salmão" 
                                className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute top-4 right-4">
                                <Sparkles className="text-neon-yellow animate-pulse" size={24} />
                            </div>
                        </div>
                        <div className="space-y-3">
                            <span className="font-rajdhani text-neon-cyan text-xs uppercase tracking-widest">
                                Mega Promoção
                            </span>
                            <h3 className="font-orbitron text-xl md:text-2xl text-white font-bold">
                                20 SASHIMIS DE SALMÃO
                            </h3>
                            <p className="font-chakra text-gray-400 text-sm">
                                Salmão premium fatiado na hora. Frescor garantido!
                            </p>
                            <div className="flex items-center justify-between">
                                <div className="price-tag">R$ 15,99</div>
                                <NeonButton href={WHATSAPP_FULL_LINK} className="!py-2 !px-4" dataTestId="cta-sashimi">
                                    <Zap size={16} />
                                    Pedir
                                </NeonButton>
                            </div>
                        </div>
                    </div>

                    <div className="product-card p-6 group" data-testid="offer-hot-philadelphia">
                        <div className="relative overflow-hidden mb-4">
                            <img 
                                src={IMAGES.hotRoll} 
                                alt="10 Hot Philadelphia" 
                                className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute top-4 right-4">
                                <Sparkles className="text-neon-yellow animate-pulse" size={24} />
                            </div>
                        </div>
                        <div className="space-y-3">
                            <span className="font-rajdhani text-neon-cyan text-xs uppercase tracking-widest">
                                Mega Promoção
                            </span>
                            <h3 className="font-orbitron text-xl md:text-2xl text-white font-bold">
                                10 HOT PHILADELPHIA
                            </h3>
                            <p className="font-chakra text-gray-400 text-sm">
                                Crocante por fora, cremoso por dentro. Irresistível!
                            </p>
                            <div className="flex items-center justify-between">
                                <div className="price-tag">R$ 15,99</div>
                                <NeonButton href={WHATSAPP_FULL_LINK} className="!py-2 !px-4" dataTestId="cta-hot-philadelphia">
                                    <Zap size={16} />
                                    Pedir
                                </NeonButton>
                            </div>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    className="text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                >
                    <NeonButton href={WHATSAPP_FULL_LINK} className="text-lg" dataTestId="hero-cta-cardapio">
                        <MessageCircle size={22} />
                        Ver Cardápio Completo
                    </NeonButton>
                    
                    <motion.div 
                        className="mt-8"
                        animate={{ y: [0, 10, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <ChevronDown className="mx-auto text-neon-cyan" size={32} />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

const DeliverySection = () => {
    return (
        <section id="delivery" data-testid="delivery-section" className="py-24 bg-cyber-bg relative">
            <div className="absolute inset-0 cyber-grid opacity-20" />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="font-rajdhani text-neon-cyan text-sm uppercase tracking-widest mb-4 block">
                        Cobertura Total
                    </span>
                    <h2 className="font-orbitron text-3xl md:text-5xl font-bold text-white section-title inline-block">
                        ÁREA DE <span className="text-neon-red">ENTREGA</span>
                    </h2>
                </motion.div>

                <motion.div
                    className="flex flex-wrap justify-center gap-4 mb-12"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                >
                    {DELIVERY_AREAS.map((area, index) => (
                        <motion.div
                            key={area}
                            className="delivery-badge"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            whileHover={{ scale: 1.05 }}
                            data-testid={`delivery-area-${index}`}
                        >
                            <MapPin size={18} />
                            {area}
                        </motion.div>
                    ))}
                </motion.div>

                <motion.div
                    className="grid md:grid-cols-3 gap-6 mb-12"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                >
                    <div className="text-center p-6 bg-cyber-card border border-white/5">
                        <Clock className="mx-auto text-neon-green mb-4" size={40} />
                        <h3 className="font-orbitron text-xl text-white mb-2">24 HORAS</h3>
                        <p className="font-chakra text-gray-400">Funcionamos sem parar!</p>
                    </div>
                    <div className="text-center p-6 bg-cyber-card border border-white/5">
                        <Zap className="mx-auto text-neon-yellow mb-4" size={40} />
                        <h3 className="font-orbitron text-xl text-white mb-2">ENTREGA RÁPIDA</h3>
                        <p className="font-chakra text-gray-400">Chegamos quentinho</p>
                    </div>
                    <div className="text-center p-6 bg-cyber-card border border-white/5">
                        <MessageCircle className="mx-auto text-neon-red mb-4" size={40} />
                        <h3 className="font-orbitron text-xl text-white mb-2">WHATSAPP</h3>
                        <p className="font-chakra text-gray-400">Pedido fácil e rápido</p>
                    </div>
                </motion.div>

                <div className="text-center">
                    <NeonButton href={WHATSAPP_FULL_LINK} dataTestId="delivery-cta-whatsapp">
                        <MessageCircle size={20} />
                        Fazer Pedido Agora
                    </NeonButton>
                </div>
            </div>
        </section>
    );
};

const TestimonialsSection = () => {
    return (
        <section id="depoimentos" data-testid="testimonials-section" className="py-24 bg-cyber-bg-secondary relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-purple to-transparent" />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="font-rajdhani text-neon-purple text-sm uppercase tracking-widest mb-4 block">
                        Clientes Satisfeitos
                    </span>
                    <h2 className="font-orbitron text-3xl md:text-5xl font-bold text-white section-title inline-block">
                        O QUE <span className="text-neon-purple">DIZEM</span>
                    </h2>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-6">
                    {TESTIMONIALS.map((testimonial, index) => (
                        <motion.div
                            key={index}
                            className="testimonial-card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.15 }}
                            data-testid={`testimonial-${index}`}
                        >
                            <p className="font-chakra text-gray-300 mb-4 italic">
                                "{testimonial.text}"
                            </p>
                            <div className="flex items-center gap-1 mb-2">
                                {[...Array(testimonial.rating)].map((_, i) => (
                                    <Star key={i} className="text-neon-yellow fill-neon-yellow" size={16} />
                                ))}
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-rajdhani text-white font-semibold">
                                    {testimonial.name}
                                </span>
                                <span className="font-chakra text-gray-500 text-sm">
                                    {testimonial.area}
                                </span>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-purple to-transparent" />
        </section>
    );
};

const GallerySection = () => {
    const galleryImages = [
        { src: IMAGES.gallery1, alt: "Sushi Premium" },
        { src: IMAGES.gallery2, alt: "Combinado Especial" },
        { src: IMAGES.gallery3, alt: "Ambiente Cyberpunk" },
        { src: IMAGES.sashimi, alt: "Sashimi Fresco" },
        { src: IMAGES.hotRoll, alt: "Hot Roll" },
        { src: IMAGES.hero, alt: "Delivery Noturno" },
    ];

    return (
        <section id="galeria" data-testid="gallery-section" className="py-24 bg-cyber-bg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="font-rajdhani text-neon-cyan text-sm uppercase tracking-widest mb-4 block">
                        Nossa Arte
                    </span>
                    <h2 className="font-orbitron text-3xl md:text-5xl font-bold text-white section-title inline-block">
                        <span className="text-neon-cyan">GALERIA</span>
                    </h2>
                </motion.div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {galleryImages.map((image, index) => (
                        <motion.div
                            key={index}
                            className="gallery-image aspect-square"
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            data-testid={`gallery-image-${index}`}
                        >
                            <img 
                                src={image.src} 
                                alt={image.alt}
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    className="text-center mt-12"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                >
                    <NeonButton href={WHATSAPP_FULL_LINK} dataTestId="gallery-cta-whatsapp">
                        <MessageCircle size={20} />
                        Quero Pedir!
                    </NeonButton>
                </motion.div>
            </div>
        </section>
    );
};

const ContactSection = () => {
    return (
        <section id="contato" data-testid="contact-section" className="py-24 bg-cyber-bg-secondary relative">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-red to-transparent" />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="font-rajdhani text-neon-red text-sm uppercase tracking-widest mb-4 block">
                        Fale Conosco
                    </span>
                    <h2 className="font-orbitron text-3xl md:text-5xl font-bold text-white section-title inline-block">
                        <span className="text-neon-red">CONTATO</span>
                    </h2>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    <motion.div
                        className="space-y-4"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <a 
                            href={WHATSAPP_FULL_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="contact-item block"
                            data-testid="contact-whatsapp"
                        >
                            <Phone className="text-neon-green" size={24} />
                            <div>
                                <span className="font-rajdhani text-gray-400 text-sm uppercase block">WhatsApp</span>
                                <span className="font-chakra text-white">(21) 97223-2170</span>
                            </div>
                        </a>

                        <a 
                            href="https://instagram.com/comidajaponesadelivery"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="contact-item block"
                            data-testid="contact-instagram"
                        >
                            <Instagram className="text-neon-purple" size={24} />
                            <div>
                                <span className="font-rajdhani text-gray-400 text-sm uppercase block">Instagram</span>
                                <span className="font-chakra text-white">@comidajaponesadelivery</span>
                            </div>
                        </a>

                        <a 
                            href="mailto:contato@comidajaponesadelivery.com.br"
                            className="contact-item block"
                            data-testid="contact-email"
                        >
                            <Mail className="text-neon-cyan" size={24} />
                            <div>
                                <span className="font-rajdhani text-gray-400 text-sm uppercase block">E-mail</span>
                                <span className="font-chakra text-white">contato@comidajaponesadelivery.com.br</span>
                            </div>
                        </a>

                        <div className="contact-item" data-testid="contact-address">
                            <MapPin className="text-neon-red" size={24} />
                            <div>
                                <span className="font-rajdhani text-gray-400 text-sm uppercase block">Endereço</span>
                                <span className="font-chakra text-white">Av. Vice Presidente José de Alencar, 1400</span>
                            </div>
                        </div>

                        <div className="contact-item" data-testid="contact-hours">
                            <Clock className="text-neon-yellow" size={24} />
                            <div>
                                <span className="font-rajdhani text-gray-400 text-sm uppercase block">Horário</span>
                                <span className="font-chakra text-white">24 Horas - Todos os Dias</span>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        className="flex flex-col items-center justify-center text-center p-8 bg-cyber-card border border-white/5"
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <h3 className="font-orbitron text-2xl text-white mb-4">
                            PEÇA AGORA!
                        </h3>
                        <p className="font-chakra text-gray-400 mb-6">
                            Solicite nosso cardápio completo pelo WhatsApp e faça seu pedido em segundos!
                        </p>
                        <NeonButton href={WHATSAPP_FULL_LINK} className="text-lg" dataTestId="contact-cta-whatsapp">
                            <MessageCircle size={22} />
                            Abrir WhatsApp
                        </NeonButton>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

const BackgroundMusic = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioRef] = useState(() => {
        const audio = new Audio(BACKGROUND_MUSIC_URL);
        audio.loop = true;
        audio.volume = 0.3;
        return audio;
    });

    const toggleMusic = () => {
        if (isPlaying) {
            audioRef.pause();
        } else {
            audioRef.play();
        }
        setIsPlaying(!isPlaying);
    };

    useEffect(() => {
        return () => {
            audioRef.pause();
        };
    }, [audioRef]);

    return (
        <motion.button
            data-testid="music-toggle"
            onClick={toggleMusic}
            className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-cyber-bg border-2 border-neon-cyan flex items-center justify-center shadow-lg hover:shadow-neon-cyan/50 transition-all"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
        >
            {isPlaying ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f0ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="4" width="4" height="16"></rect>
                    <rect x="14" y="4" width="4" height="16"></rect>
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f0ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
            )}
        </motion.button>
    );
};

const Footer = () => {
    return (
        <footer data-testid="footer" className="py-8 bg-cyber-bg border-t border-white/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <img 
                        src={LOGO_URL} 
                        alt="Comida Japonesa Delivery" 
                        className="h-12 w-auto"
                    />
                    
                    <div className="flex items-center gap-6">
                        <a 
                            href="https://instagram.com/comidajaponesadelivery"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-neon-purple transition-colors"
                            data-testid="footer-instagram"
                        >
                            <Instagram size={24} />
                        </a>
                        <a 
                            href={WHATSAPP_FULL_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-neon-green transition-colors"
                            data-testid="footer-whatsapp"
                        >
                            <MessageCircle size={24} />
                        </a>
                        <a 
                            href="mailto:contato@comidajaponesadelivery.com.br"
                            className="text-gray-400 hover:text-neon-cyan transition-colors"
                            data-testid="footer-email"
                        >
                            <Mail size={24} />
                        </a>
                    </div>

                    <p className="font-chakra text-gray-500 text-sm text-center">
                        © 2025 Comida Japonesa Delivery. Todos os direitos reservados.
                    </p>
                </div>
            </div>
        </footer>
    );
};

function App() {
    return (
        <div className="min-h-screen bg-cyber-bg">
            <Header />
            <main>
                <HeroSection />
                <section id="ofertas">
                    <DeliverySection />
                </section>
                <TestimonialsSection />
                <GallerySection />
                <ContactSection />
            </main>
            <Footer />
            <BackgroundMusic />
        </div>
    );
}

export default App;
